import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  NewReleases as NewReleasesIcon,
  BugReport as BugFixIcon,
  Speed as PerformanceIcon,
  Palette as UIIcon,
} from '@mui/icons-material';
import updateData from '@/whats-new.json';
import { AppContext } from '@/contexts/AppContext';
import { AuthApi, User } from '@api';
import { useTranslation } from 'react-i18next';

const WhatsNewModal = ({ currentVersion = updateData.version }) => {
  const [open, setOpen] = useState(false);
  const { user, setUser } = useContext(AppContext);
  const { t } = useTranslation();

  const getLastSeenVersion = () => {
    if (user?.version) return user?.version;
    return '0.0.0';
  };

  const hasNewVersion = (currentVersion: string, lastSeenVersion: string) => {
    return currentVersion > lastSeenVersion;
  };

  useEffect(() => {
    const lastSeenVersion = getLastSeenVersion().replace(/"/g, ''); //fick lägga till en replace då versionen sparas med fnjuttar (") i db.

    if (hasNewVersion(currentVersion, lastSeenVersion)) {
      setOpen(true);
    }
  }, [currentVersion]);

  const updateUserVersion = async () => {
    const res = await new AuthApi().updateVersion(updateData.version);
    if (res) setUser({ ...user, version: updateData.version } as User);
  };

  const handleClose = () => {
    updateUserVersion();
    setUser({ ...user, version: updateData.version } as User);
    setOpen(false);
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'feature':
        return <NewReleasesIcon color="primary" />;
      case 'bugfix':
        return <BugFixIcon color="primary" />;
      case 'performance':
        return <PerformanceIcon color="primary" />;
      case 'ui':
        return <UIIcon color="primary" />;
      default:
        return <NewReleasesIcon />;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <NewReleasesIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" component="div">
            {t('whatsNew.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={2}>
          <Typography variant="subtitle1" color="text.secondary">
            {t('whatsNew.versionInfo', {
              version: updateData.version,
              date: updateData.releaseDate,
            })}
          </Typography>
        </Box>

        <List>
          {updateData.features.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider variant="inset" component="li" />}
              <ListItem alignItems="flex-start">
                <ListItemIcon>{getIconForType(item.type)}</ListItemIcon>
                <ListItemText
                  primary={t(item.title)}
                  secondary={t(item.description)}
                  primaryTypographyProps={{ fontWeight: 'medium' }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="primary">
          {t('whatsNew.closeButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsNewModal;
